import React, {useContext, useEffect, useMemo, useRef} from "react";
import {UserHomepageControllerContext, UserHomepageStateContext, UserHomepageViewContext} from "../../../../index";
import {Col} from "reactstrap";
import {UserHomepageControllerEvents, UserHomepageViewMode} from "../../../../models";
import UserHomepageUtils from "../../../../core/utils";
import WebSimulatorBody from "../../../../../../components/base/device-simulator/web-entities/body";
import UserWebHomepageComponent from "../../components/component";
import classnames from "classnames";

const UserWebHomepageBody = () => {
    // const {zoom} = useContext(UserHomepageViewContext);
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const newComponent = useRef(undefined);

    /**@type{React.MutableRefObject<HTMLDivElement>}*/
    const contentRef = useRef();
    // /**@type{React.MutableRefObject<HTMLDivElement>}*/
    // const patternRef = useRef();


    const selectedTab = useMemo(() => {
        if (!state.selection.tabId)
            return null;
        return state.updatedData?.tabs?.find(e => e.id === state.selection.tabId)
    }, [state.selection.tabId, state.updatedData?.tabs])
    const loading = useMemo(() => selectedTab?.loading, [selectedTab])
    const noTabs = useMemo(() => state?.updatedData?.tabs && !state?.updatedData?.tabs.length, [state?.updatedData?.tabs])

    /**
     * With each change in the state or controller:
     * - attaches the event listeners for focusing the body of the phone view on selection of tabs.
     * - attaches an event listener for creation of components so that they can be selected and focused.
     */
    useEffect(() => {
        controller.on(UserHomepageControllerEvents.onTabSelected, focusTab);
        controller.on(UserHomepageControllerEvents.onComponentCreated, awaitStateUpdateOnComponentCreation);
        return () => {
            controller.off(UserHomepageControllerEvents.onTabSelected, focusTab);
            controller.off(UserHomepageControllerEvents.onComponentCreated, awaitStateUpdateOnComponentCreation);
        }
    }, [controller, state.updatedData])

    /**
     * With each change in the updatedData of the state:
     * - checks if a new component has been created, and if so, awaits its appearance in the ui and then scrolls to it.
     */
    useEffect(() => {
        if (!newComponent.current)
            return;
        const component = state?.updatedData?.tabs?.find(e => e.id === newComponent.current.tabId)?.components?.find(e => e.id === newComponent.current.componentId);
        if (!component)
            return;

        newComponent.current = undefined;
        setTimeout(() => {
            document
                .getElementById(component.webViewElementId)
                ?.scrollIntoView(true);
        }, 800)
    }, [state.updatedData])

    /**
     * Awaits the update of the state before focusing the newly created component
     * @param {UserHomepageEventEmitter} emitter
     * @param {number} tabId
     * @param {number} componentId
     */
    const awaitStateUpdateOnComponentCreation = (emitter, tabId, componentId) => {
        newComponent.current = {tabId, componentId};
    }

    /**
     * Scrolls the view to the top so that the components can be viewed from the top.
     * @param {UserHomepageEventEmitter} emitter
     * @param {number} tabId
     */
    const focusTab = (emitter, tabId) => {
        const tab = UserHomepageUtils.getSelection(state?.updatedData, tabId);
        if (!tab)
            return;
        document.getElementById(state.webViewElementId)?.scrollTo({top: 0, left: 0});
    }


    /**
     * Renders the components of the selected tab.
     * @return {JSX.Element[]}
     */
    const components = useMemo(() => {
        const _components = state.viewMode === UserHomepageViewMode.edit
            ? selectedTab?.components
            : UserHomepageUtils.getPreviewComponents(selectedTab?.components)
        return _components?.map(component => (
            <UserWebHomepageComponent
                bodyId={state.webViewElementId}
                key={component.id}
                component={component}
                tabId={selectedTab.id}
                viewMode={state.viewMode}
            />
        ))
    }, [selectedTab, state.webViewElementId, state.viewMode])

    return (
        <>
            <WebSimulatorBody className={'body'} id={state.webViewElementId}>
                <div
                    className={classnames('background-container user-web-homepage-sections-stack', {'no-tabs': noTabs})}
                    ref={contentRef}
                >
                    {
                        noTabs &&
                        <>
                            <div
                                className={'d-flex h-100 align-items-center justify-content-center text-center'}>
                                <p className={'text-sm text-color-1 font-weight-semi-bold'}>
                                    there is currently no tabs available for your homepage
                                </p>
                            </div>
                        </>
                    }
                    {
                        !noTabs && loading &&
                        <UserWebHomepageBodyLoading/>
                    }
                    {
                        !noTabs && !loading &&
                        components
                    }
                </div>
            </WebSimulatorBody>
        </>
    )
}

const UserWebHomepageBodyLoading = () => {
    const {viewportWidth} = useContext(UserHomepageViewContext);

    const categoryWidths = useMemo(() => {
        switch (viewportWidth) {
            case "auto":
                return {xs: 6, sm: 6, md: 4, lg: 3};
            case "xxl":
            case "xl":
            case "lg":
                return {xs: 3};
            case 'md':
                return {xs: 4};
            case 'sm':
            case 'xs':
                return {xs: 6};
            default:
                return {}
        }
    }, [viewportWidth])
    const largeProductWidths = useMemo(() => {
        switch (viewportWidth) {
            case "auto":
            default:
                return {xs: 12, md: 10, lg: 6};
            case "xxl":
            case "xl":
                return {xs: 6,};
            case "md":
            case "lg":
                return {xs: 10,};
            case "sm":
            case "xs":
                return {xs: 12,};
        }
    }, [viewportWidth])
    const mediumProductWidths = useMemo(() => {
        switch (viewportWidth) {
            case "auto":
            default:
                return {xs: 10, sm: 9, md: 6, lg: 4};
            case "xxl":
            case "xl":
                return {xs: 4};
            case "md":
            case "lg":
                return {xs: 6};
            case "sm":
                return {xs: 9};
            case "xs":
                return {xs: 10};
        }
    }, [viewportWidth])

    return (
        <>
            <div className={classnames('user-web-homepage-component loading-body', {[viewportWidth]: true})}>
                <div className={'loading web-home-page slideshow'}>
                    <div/>
                </div>
                {
                    Array(4).fill(null).map((_, e) => (
                        <Col key={e} {...categoryWidths}>
                            <div className={'loading web-home-page category'}>
                                <div/>
                            </div>
                        </Col>
                    ))
                }
                {
                    Array(2).fill(null).map((_, e) => (
                        <Col key={e} {...largeProductWidths}>
                            <div className={'loading web-home-page part-large'}>
                                <div/>
                            </div>
                        </Col>
                    ))
                }
                {
                    Array(6).fill(null).map((_, e) => (
                        <Col key={e} {...mediumProductWidths}>
                            <div className={'loading web-home-page part-medium'}>
                                <div/>
                            </div>
                        </Col>
                    ))
                }
            </div>
        </>
    );
}

export default UserWebHomepageBody;
